import { getApiBaseUrl } from "../api/baseUrl"
import { throwIfRateLimited } from "../api/rateLimit"
import type {
  ChallengeListResponse,
  ChallengeRecordList,
  FriendCodeLookupResult,
  FriendRequestListResponse,
  FriendRequestRecordList,
  ManagedChallengeRecord,
  ManagedChallengeSyncResponse,
  ManagedFriendRequestRecord,
  ManagedFriendRequestSyncResponse,
  PublicPlayerProfile,
  PublicPlayerProfileSyncResponse,
} from "../schemas"
import { SocialError } from "./errors"

async function postJson<TResponse>(
  path: string,
  sessionToken: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<TResponse> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new SocialError("SOCIAL_SYNC_NO_WORKER")
  }

  const response = await fetch(new URL(path, baseUrl), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new SocialError("SOCIAL_SYNC_FAILED", message || undefined)
  }

  return (await response.json()) as TResponse
}

async function getJson<TResponse>(
  path: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<TResponse> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new SocialError("SOCIAL_SYNC_NO_WORKER")
  }

  const response = await fetch(new URL(path, baseUrl), {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
    signal,
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new SocialError("SOCIAL_FETCH_FAILED", message || undefined)
  }

  return (await response.json()) as TResponse
}

export async function syncPublicPlayerProfileToServer(
  record: PublicPlayerProfile,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<PublicPlayerProfile> {
  const response = await postJson<PublicPlayerProfileSyncResponse>(
    "/api/social/profiles",
    sessionToken,
    { record },
    signal,
  )
  return response.record
}

export async function fetchPublicPlayerProfileByFriendCodeFromServer(
  sessionToken: string,
  friendCode: string,
  signal?: AbortSignal,
): Promise<FriendCodeLookupResult | null> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new SocialError("SOCIAL_SYNC_NO_WORKER")
  }

  const response = await fetch(
    new URL(
      `/api/social/profiles?friendCode=${encodeURIComponent(friendCode.trim().toUpperCase())}`,
      baseUrl,
    ),
    {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
      signal,
    },
  )

  throwIfRateLimited(response)

  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    const message = await response.text()
    throw new SocialError("SOCIAL_FETCH_FAILED", message || undefined)
  }

  const body = (await response.json()) as { record: FriendCodeLookupResult }
  return body.record
}

export async function fetchPublicPlayerProfileByOwnerIdFromServer(
  sessionToken: string,
  ownerUserId: string,
  signal?: AbortSignal,
): Promise<PublicPlayerProfile | null> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new SocialError("SOCIAL_SYNC_NO_WORKER")
  }

  const response = await fetch(
    new URL(
      `/api/social/profiles?ownerUserId=${encodeURIComponent(ownerUserId)}`,
      baseUrl,
    ),
    {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
      signal,
    },
  )

  throwIfRateLimited(response)

  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    const message = await response.text()
    throw new SocialError("SOCIAL_FETCH_FAILED", message || undefined)
  }

  const body = (await response.json()) as PublicPlayerProfileSyncResponse
  return body.record
}

export async function syncChallengeRecordToServer(
  record: ManagedChallengeRecord,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ManagedChallengeRecord> {
  const response = await postJson<ManagedChallengeSyncResponse>(
    "/api/social/challenges",
    sessionToken,
    { record },
    signal,
  )
  return response.record
}

export async function fetchChallengeRecordsFromServer(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ChallengeRecordList> {
  const response = await getJson<ChallengeListResponse>(
    "/api/social/challenges",
    sessionToken,
    signal,
  )
  return response.records
}

export async function syncFriendRequestRecordToServer(
  record: ManagedFriendRequestRecord,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ManagedFriendRequestRecord> {
  const response = await postJson<ManagedFriendRequestSyncResponse>(
    "/api/social/friends",
    sessionToken,
    { record },
    signal,
  )
  return response.record
}

export async function fetchFriendRequestRecordsFromServer(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<FriendRequestRecordList> {
  const response = await getJson<FriendRequestListResponse>(
    "/api/social/friends",
    sessionToken,
    signal,
  )
  return response.records
}
